import { useEffect, useState } from "react";
import Avatar from "react-avatar";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { selectAuth } from "../../../redux/features/auth/auth-slice";
import {
  selectIsOnCancelPressed,
  selectIsOnConfirmPressed,
  setIsOnCancelPressed,
  setIsOnConfirmPressed,
  setIsShowEstimationInterruptModal,
} from "../../../redux/features/solar-estimation/solarEstimationSlice";
import {
  selectCountry,
  selectProfile,
  setCountry,
} from "../../../redux/features/user/user-slice";
import { getConsumerPrivateRoutes } from "../../../screens/routes";
import logo from "../../../shared/oversight-core/assets/images/chloride-logo.png";
import { ECountry } from "../../oversight-core/enums/country";
import { UserRole } from "../../oversight-core/enums/user-roles";
import { AppRoute } from "../../oversight-core/interfaces/app-routes";
import MobileNavBar from "../../oversight-core/shared-components/mobile-nav-bar/mobile-nav-bar";
import SideBar from "../../oversight-core/shared-components/sidebar/side-bar";
import AppDropDown from "../../oversight-core/ui-elements/app-dropdown/app-drop-down";
import CountryFlag from "../../oversight-core/ui-elements/country-flag/country-flag";
import MaterialIcon from "../../oversight-core/ui-elements/material-icon/material-icon";
import ScrollToTop from "../../oversight-core/ui-elements/scroll-to-top/ScrollToTop";
import { isOnEstimation } from "../../oversight-core/utils/solarEstimationHelper";
import translate from "../../oversight-core/utils/translate";
import styles from "./solar-sim-dashboard-layout.module.scss";

const SolarSimDashboardLayout = () => {
  const auth = useSelector(selectAuth);
  const country = useSelector(selectCountry);
  const profile = useSelector(selectProfile);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isOnConfirmPressed = useSelector(selectIsOnConfirmPressed);
  const isOnCancelPressed = useSelector(selectIsOnCancelPressed);

  const [collapsed, setCollapsed] = useState(true);
  const [open, setOpen] = useState(false);
  const [navigateOnInterruption, setNavigateOnInterruption] = useState("");

  const getCurrentPageInfo = (path: string) => {
    const page = getConsumerPrivateRoutes(auth.userRole)
      .filter((route) => route.root)[0]
      ?.subRoutes?.find((pi) => pi.path === path);

    return page;
  };

  const getCurrentPageTitle = () => {
    const lastSlashIndex = pathname.lastIndexOf("/");

    if (getCurrentPageInfo(pathname)?.name === undefined) {
      return getCurrentPageInfo(pathname.substring(0, lastSlashIndex) + "/:id")
        ?.name;
    } else {
      return getCurrentPageInfo(pathname)?.name;
    }
  };

  useEffect(() => {
    if (isOnConfirmPressed && navigateOnInterruption) {
      dispatch(setIsShowEstimationInterruptModal(false));
      dispatch(setIsOnConfirmPressed(false));
      setNavigateOnInterruption("");
      navigate(navigateOnInterruption);
    }
  }, [isOnConfirmPressed, navigateOnInterruption]);

  useEffect(() => {
    if (isOnCancelPressed && navigateOnInterruption) {
      setNavigateOnInterruption("");
      dispatch(setIsOnCancelPressed(false));
    }
  }, [isOnCancelPressed, navigateOnInterruption]);

  return (
    <>
      <div className="d-flex vh-100">
        <div
          className={`${styles.sidebar} ${
            collapsed ? styles.sidebarCollapsed : styles.sidebarNotCollapsed
          } d-none d-xl-block`}
        >
          <SideBar
            collapsed={collapsed}
            routes={getConsumerPrivateRoutes(auth.userRole)}
            logos={[logo, logo]}
          />
        </div>
        <div className="p-3 w-100">
          <Row className="justify-content-between align-items-center">
            <Col xs={8} sm="auto">
              <Row className="align-items-center justify-content-end">
                <Col className="col-auto d-none d-xl-block">
                  <MaterialIcon
                    icon="menu"
                    size={22}
                    weight={500}
                    className="cursor-pointer"
                    onClick={() => setCollapsed((ps) => !ps)}
                    color="var(--color-menu-icon)"
                  />
                </Col>
                <Col className="col-auto">
                  <Row className="align-item-center justify-content-end gap-2">
                    <Col className="font-size-20 layout-title font-weight-600 p-0">
                      {getCurrentPageTitle()}
                    </Col>
                    <Col
                      xs="auto"
                      className="ps-0 d-flex d-block d-xl-none align-items-center"
                    >
                      <MaterialIcon
                        icon="menu"
                        size={22}
                        weight={500}
                        className="cursor-pointer"
                        onClick={() => setOpen((ps) => !ps)}
                        color="var(--color-menu-icon)"
                      />
                    </Col>
                  </Row>
                  <MobileNavBar
                    isOpen={open}
                    closeOpen={() => setOpen((ps) => !ps)}
                    routes={getConsumerPrivateRoutes(auth.userRole)}
                    logos={[logo, logo]}
                  />
                </Col>
              </Row>
            </Col>
            <Col className="col-auto col-lg-7 order-first order-xl-last">
              <Row className="align-items-center justify-content-start justify-content-xl-end">
                <Col className="col-auto order-xl-1 px-1">
                  {/* <div className="position-relative">
                  <MaterialIcon
                    icon="notifications"
                    size={25}
                    className="cursor-pointer"
                  />
                </div> */}
                </Col>
                <Col className="col-auto order-xl-2 pe-0 d-none d-sm-block">
                  <Avatar
                    name={`${profile?.firstName} ${profile?.lastName}`}
                    size="40"
                    round={true}
                    onClick={() => {
                      if (isOnEstimation(pathname)) {
                        setNavigateOnInterruption(AppRoute.USER_PROFILE);
                        dispatch(setIsShowEstimationInterruptModal(true));
                        return;
                      }

                      navigate(AppRoute.USER_PROFILE);
                    }}
                    className="cursor-pointer"
                  />
                </Col>
                <Col className="col-auto order-xl-3 d-none d-xl-block">
                  <div className="layout-title font-weight-600">
                    {profile?.firstName} {profile?.lastName}
                  </div>
                  <div className="text-color-2 font-weight-400 font-size-13">
                    {profile?.email}
                  </div>
                </Col>
                {profile?.role === UserRole.ROLE_USER && (
                  <Col className="col-auto order-xl-4 d-none d-sm-block">
                    <div
                      className={`country-bg border-2 border-gray-15 rounded-2 d-flex align-items-center justify-content-center text-white py-1 px-3`}
                    >
                      <Row className="align-items-center">
                        <Col className="col-auto pe-0">
                          <CountryFlag
                            countryCode={country}
                            isDropDown={false}
                          />
                        </Col>
                        <Col className="ps-0">
                          <AppDropDown
                            items={[
                              {
                                onClick: () => {
                                  dispatch(setCountry(ECountry.KE));
                                },
                                text: (
                                  <CountryFlag
                                    countryCode={ECountry.KE}
                                    countryName={translate(ECountry.KE)}
                                  />
                                ),
                              },
                              {
                                onClick: () => {
                                  dispatch(setCountry(ECountry.UG));
                                },
                                text: (
                                  <CountryFlag
                                    countryCode={ECountry.UG}
                                    countryName={translate(ECountry.UG)}
                                  />
                                ),
                              },
                              {
                                onClick: () => {
                                  dispatch(setCountry(ECountry.TZ));
                                },
                                text: (
                                  <CountryFlag
                                    countryCode={ECountry.TZ}
                                    countryName={translate(ECountry.TZ)}
                                  />
                                ),
                              },
                            ]}
                            icon="arrow_drop_down"
                            showBackgroundAndBorder={false}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Row className="mt-4">
            <ScrollToTop />
            <Col xs={12} className={styles["outlet"]}>
              <Outlet />
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default SolarSimDashboardLayout;
